import { groupByInstance } from "../lib/setClips";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { chunk, mergeBatches, shortReadNotice } from "../lib/batchedIn";
import { wireNumber } from "../lib/wireNumber";
import { distanceUnitFor } from "../lib/distanceCell";
import type { OneRMRule, WeighIn } from "../lib/oneRepMax";
import {
  buildHistories, instanceDate, mostRecentFirst, sectionIncludes,
  type ExerciseHistory, type HistoryInstance, type LoggedSet,
} from "../lib/progressHistory";

/* 🔴 W72 STEP 4 — ONE CLIENT'S TRAINING HISTORY, READ ONCE, SHAPED BY THE PHONE'S RULES.
 *
 * The awaiting half (routes/, outside the untested-rule ratchet by design); every conversion is a pure function in
 * `lib/progressHistory.ts`, proven against the Swift by `progressCorpus.test.ts`. What it REPLACES is
 * `ClientResults`' reader, whose flat queries and batching are kept as they were (four reads rather than one embed,
 * the `.in` batched and COUNTED against the 1,000-row cap — its own comments carry the measurements) with three
 * additions the phone's rule needed: `set_logs.status` (a `warmup` rung is not progress), the session-exercise's
 * `section_role` (warm-up and cool-down work is not a lift's history), and the coach row's `progress_views` (the
 * coach's ticks narrow the chart's menu — `ProgressChartPlan.allowed`, step 3's column, read by both sides). */

export type Clip = { id: string; storagePath: string; mediaKind: string; recordedAtISO: string | null; setNumber: number | null };
export type SetNote = { setNumber: number | null; text: string };

export type ProgressHistory =
  | { readonly status: "loading" }
  | { readonly status: "failed"; readonly message: string }
  | {
      readonly status: "ready";
      /** Most recently trained first — the phone's order. */
      readonly histories: readonly ExerciseHistory[];
      /** Per exercise key: its library row's ticked progress views (empty = all), video and thumbnail. */
      readonly library: ReadonlyMap<string, LibraryRow>;
      /** W125 — the client's weigh-ins (`body_measurements.weight_kg`), for a 1RM whose load includes the body. */
      readonly weighIns: readonly WeighIn[];
      readonly clipsByInstance: ReadonlyMap<string, readonly Clip[]>;
      readonly notesByInstance: ReadonlyMap<string, readonly SetNote[]>;
      readonly notice: string | null;
    };

/** One library row's chart settings: the ticks, the W125 `1RM` rule, the video. */
export type LibraryRow = {
  progressViews: readonly string[]; oneRM: OneRMRule; demoVideoUrl: string | null; thumbnailUrl: string | null;
  /** W136 B2 S3r5 P2 — the row's distance field is `distance_km` (`distanceUnitFor`): its distance views say km. */
  distanceKm: boolean;
};

type LogRow = {
  workout_session_exercise_id: string; status: string; reps_completed: unknown; weight_kg: unknown;
  hold_seconds: unknown; completed_at: string | null; note: string | null; set_number: unknown;
  rpe?: unknown; assist_kg?: unknown; modifier_label_override?: string | null;
  distance_m?: unknown; rounds?: unknown;
};

const ms = (iso: string | null | undefined): number | null => {
  if (typeof iso !== "string" || iso === "") return null;
  const t = Date.parse(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isFinite(t) ? t : null;
};

export function useProgressHistory(clientId: string): ProgressHistory {
  const [state, setState] = useState<ProgressHistory>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ status: "loading" });
      const fail = (message: string) => { if (!cancelled) setState({ status: "failed", message }); };

      const { data: sessions, error: sErr } = await supabase
        .from("workout_sessions").select("id, scheduled_date, completed_at, created_at").eq("user_id", clientId);
      if (cancelled) return;
      if (sErr !== null) return fail(sErr.message);
      const empty = { status: "ready" as const, histories: [], library: new Map(), weighIns: [], clipsByInstance: new Map(), notesByInstance: new Map(), notice: null };
      if ((sessions ?? []).length === 0) { setState(empty); return; }
      const sessionById = new Map((sessions ?? []).map((s) => [s.id, s]));

      const { data: wses, error: wErr } = await supabase
        .from("workout_session_exercises")
        .select("id, workout_session_id, exercise_id, coach_exercise_id, section_role")
        .in("workout_session_id", [...sessionById.keys()]);
      if (cancelled) return;
      if (wErr !== null) return fail(wErr.message);
      // ProgressListFilter.includes — main work only; the phone's page shows nothing else.
      const kept = (wses ?? []).filter((w) => sectionIncludes(w.section_role ?? null));
      if (kept.length === 0) { setState(empty); return; }

      const batches = chunk(kept.map((w) => w.id));
      const results: { rows: LogRow[]; count: number | null; errored: boolean }[] = [];
      for (const ids of batches) {
        const r = await supabase
          .from("set_logs")
          .select("workout_session_exercise_id, status, reps_completed, weight_kg, hold_seconds, completed_at, note, set_number, rpe, assist_kg, modifier_label_override, distance_m, rounds",
            { count: "exact" })
          .in("workout_session_exercise_id", ids);
        if (cancelled) return;
        results.push({ rows: (r.data ?? []) as LogRow[], count: r.count ?? null, errored: r.error !== null });
      }
      const read = mergeBatches(results);
      if (batches.length > 0 && read.failedBatches.length === batches.length) return fail("Their logged sets could not be read.");

      const coachIds = [...new Set(kept.map((w) => w.coach_exercise_id).filter((x): x is string => typeof x === "string"))];
      const engineIds = [...new Set(kept.map((w) => w.exercise_id).filter((x): x is string => typeof x === "string"))];
      const names = new Map<string, string>();
      const library = new Map<string, LibraryRow>();
      if (coachIds.length > 0) {
        // W125 — the `1RM` switch rides the same read: every coach lift obeys its own, OFF by default.
        const { data } = await supabase.from("coach_exercises")
          .select("id, name, progress_views, one_rm_enabled, body_is_load, demo_video_url, thumbnail_url, tracking_fields").in("id", coachIds);
        for (const e of data ?? []) {
          names.set(e.id, e.name);
          library.set(e.id, {
            progressViews: e.progress_views ?? [],
            oneRM: { kind: "coach", enabled: e.one_rm_enabled === true, bodyIsLoad: e.body_is_load === true },
            demoVideoUrl: e.demo_video_url, thumbnailUrl: e.thumbnail_url,
            distanceKm: distanceUnitFor(e.tracking_fields ?? []) === "km",
          });
        }
      }
      // W125 — the client's weigh-ins (`body_measurements_coach_read`, measured per identity 2026-10-02). A failed read is
      // an empty list, which the chart says out loud ("No bodyweight logged") rather than guessing a number.
      const weighIns: WeighIn[] = [];
      {
        const { data } = await supabase.from("body_measurements").select("measured_at, weight_kg").eq("user_id", clientId);
        for (const m of (data ?? []) as { measured_at: string; weight_kg: unknown }[]) {
          const kg = wireNumber(m.weight_kg), at = ms(m.measured_at);
          if (kg !== null && kg > 0 && at !== null) weighIns.push({ at, kg });
        }
      }
      if (engineIds.length > 0) {
        const { data } = await supabase.from("exercises").select("id, name").in("id", engineIds);
        for (const e of data ?? []) names.set(e.id, e.name);
      }
      if (cancelled) return;

      const logsBy = new Map<string, LogRow[]>();
      for (const l of read.rows) logsBy.set(l.workout_session_exercise_id, [...(logsBy.get(l.workout_session_exercise_id) ?? []), l]);
      const notesByInstance = new Map<string, SetNote[]>();
      const instances: HistoryInstance[] = [];
      for (const w of kept) {
        const key = w.coach_exercise_id ?? w.exercise_id;
        const logs = logsBy.get(w.id) ?? [];
        if (typeof key !== "string" || logs.length === 0) continue;
        const s = sessionById.get(w.workout_session_id);
        const date = instanceDate(logs.map((l) => ms(l.completed_at)), {
          scheduled: ms(s?.scheduled_date), completed: ms(s?.completed_at), created: ms(s?.created_at),
        });
        if (date === null) continue;
        const sets: LoggedSet[] = [...logs]
          .sort((a, b) => (wireNumber(a.set_number) ?? 0) - (wireNumber(b.set_number) ?? 0))
          .map((l) => ({
            setNumber: wireNumber(l.set_number) ?? 0, status: l.status,
            reps: wireNumber(l.reps_completed), weightKg: wireNumber(l.weight_kg), holdSeconds: wireNumber(l.hold_seconds),
            rpe: wireNumber(l.rpe), assistKg: wireNumber(l.assist_kg), band: l.modifier_label_override ?? "",
            // W136 B2 S3r5 P2 (D8) — the record's own distance / rounds: a run's time then is never read as a hold.
            distanceM: wireNumber(l.distance_m), rounds: wireNumber(l.rounds),
          }));
        instances.push({ key, name: names.get(key) ?? "Unnamed exercise", instanceId: w.id, sessionId: w.workout_session_id, date, logs: sets });
        const notes = logs.filter((l) => typeof l.note === "string" && l.note.trim() !== "")
          .map((l) => ({ setNumber: wireNumber(l.set_number), text: (l.note ?? "").trim() }));
        if (notes.length > 0) notesByInstance.set(w.id, notes);
      }
      instances.sort((a, b) => a.date - b.date || (a.instanceId < b.instanceId ? -1 : 1));

      // W116 — THE BATCH: ONE `form_clips` read for every instance on the page, filed by instance (`setClips.groupByInstance`)
      // so each readout line looks up its own; never a read per line.
      // ⚠️ IN THE SAME FIFTIES AS THE SET LOGS (`batchedIn`, W10f): one `.in` over every instance id was a ~22,000-character
      // URL on a long history — the silent short read W10f measured, which here would have hidden the videos.
      const clipRows: { id: string; storage_path: string; media_kind: string; recorded_at: string; workout_session_exercise_id: string; set_number: number | string }[] = [];
      for (const ids of batches) {
        const { data } = await supabase
          .from("form_clips")
          .select("id, storage_path, media_kind, recorded_at, workout_session_exercise_id, set_number")
          .in("workout_session_exercise_id", ids);
        if (cancelled) return;
        clipRows.push(...(data ?? []));
      }
      const clipsByInstance: Map<string, Clip[]> = new Map([...groupByInstance(clipRows, (c) => c.workout_session_exercise_id)]
        .map(([k, rows]) => [k, rows.map((c) => ({
          id: c.id, storagePath: c.storage_path, mediaKind: c.media_kind, recordedAtISO: c.recorded_at,
          setNumber: wireNumber(c.set_number),
        }))]));

      setState({
        status: "ready", histories: mostRecentFirst(buildHistories(instances)), library, weighIns, clipsByInstance,
        notesByInstance, notice: shortReadNotice(read),
      });
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  return state;
}
